import React from 'react';

import Select from '../../../../../select/select';
import { InputMode, SelectRef } from '../../../../../select/select-types';
import FitmentStepContainer from '../fitment-step-container/fitment-step-container';

import {
  useFeatureFlags,
  useMediaQuery
} from '../../../../../../shared/hooks/hooks';

import type { OptionValue } from '../../../../../../shared/types/select';
import { FEATURE_FLAGS } from '../../../../../../shared/constants/feature-flags';

import './fitment-select.scss';

interface FitmentSelectProps {
  className?: string;
  heading?: React.ReactNode;
  hideHeading?: boolean;
  inputMode?: InputMode;
  isDisabled?: boolean;
  moreInfoContent?: () => JSX.Element;
  name: string;
  onChange: () => void;
  onFocus: () => void;
  onMoreInfoContentClick?: () => void;
  options: OptionValue[];
  placeholder: string;
  renderNoResultsFound: (props) => React.ReactElement;
  renderOption?: (props) => React.ReactElement;
  renderValue?: (props) => React.ReactElement;
  selectRef?: SelectRef<OptionValue>;
  selectedOption: OptionValue;
  shouldAutofocus?: boolean;
  shouldOpenMenuByDefault?: boolean;
  subject?: string;
}

function FitmentSelect(props: FitmentSelectProps) {
  const {
    className = '',
    heading,
    hideHeading = false,
    inputMode,
    isDisabled = false,
    moreInfoContent,
    name,
    onChange,
    onFocus,
    onMoreInfoContentClick,
    options,
    placeholder,
    renderNoResultsFound,
    renderOption,
    renderValue,
    selectRef,
    selectedOption,
    shouldAutofocus = false,
    shouldOpenMenuByDefault,
    subject
  } = props;

  const { isMinMd } = useMediaQuery();
  const { featureFlags } = useFeatureFlags();
  const isFitmentRedesign = Boolean(
    featureFlags?.[FEATURE_FLAGS.__FF_FITMENT_REDESIGN__]
  );

  const defaultHeading = isFitmentRedesign ? (
    <>What is your vehicle {placeholder?.toLowerCase()}?</>
  ) : (
    <>
      What is the <strong>{placeholder?.toLowerCase()}</strong> of your{' '}
      {subject || 'vehicle'}?
    </>
  );
  const fitmentStepContainerHeading = heading || defaultHeading;

  return (
    <FitmentStepContainer
      className={`fitment-select ${className}`}
      heading={hideHeading ? undefined : fitmentStepContainerHeading}
      moreInfoContent={moreInfoContent}
      onMoreInfoContentClick={onMoreInfoContentClick}
    >
      <Select
        aria-labelledby="fitment-step-container-heading"
        blurInputOnSelect={!isMinMd}
        data-testid={`fitment-select__${name}`}
        inputMode={inputMode}
        isDisabled={isDisabled}
        isMenuOpenByDefault={shouldOpenMenuByDefault}
        isRequired
        isSearchable
        label={placeholder}
        name={name}
        onChange={onChange}
        onFocus={onFocus}
        openMenuOnFocus={!isMinMd}
        options={options}
        renderNoResultsFound={renderNoResultsFound}
        renderOption={renderOption}
        renderValue={renderValue}
        selectRef={selectRef}
        shouldAutofocus={shouldAutofocus}
        styleName="select"
        value={selectedOption ?? null}
      />
    </FitmentStepContainer>
  );
}

export default FitmentSelect;
